import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../utils/api';
import { useFiscalYear } from '../contexts/FiscalYearContext';

/**
 * Bahrain VAT Return — 18-line quarterly summary.
 * Admin / super_admin only (sidebar gated). Auto-classifies invoices and
 * purchase entries into the 12 NBR VAT categories, with manual override
 * fields for line 16 (corrections) and line 17 (carry forward).
 */
export default function VATReturn() {
  // Sensible default = current calendar quarter
  const now = new Date();
  const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  const qEnd = new Date(qStart.getFullYear(), qStart.getMonth() + 3, 0);
  const iso = (d) => d.toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(iso(qStart));
  const [endDate, setEndDate] = useState(iso(qEnd));
  const { selectedFy } = useFiscalYear();
  const [corrections, setCorrections] = useState('');
  const [carryForward, setCarryForward] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // When the user switches FY in the header, snap the period to that FY
  // automatically. Skip the very first run so the quarterly default the
  // accountant landed on isn't immediately overwritten.
  const fyInitDone = useRef(false);
  useEffect(() => {
    if (!selectedFy) return;
    if (!fyInitDone.current) {
      fyInitDone.current = true;
      return;
    }
    setStartDate(selectedFy.start_date);
    setEndDate(selectedFy.end_date);
    setReport(null);  // Clear stale report so the user must regenerate.
  }, [selectedFy?.id]);

  const periodLabel = useMemo(() => {
    if (!startDate || !endDate) return '';
    const d = (s) => {
      try {
        return new Date(s).toLocaleDateString('en-GB',
          { day: 'numeric', month: 'long', year: 'numeric' });
      } catch { return s; }
    };
    return `Period: ${d(startDate)} to ${d(endDate)}`;
  }, [startDate, endDate]);

  const fmt = (n) => (typeof n === 'number'
    ? n.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    : (n || ''));

  const runReport = async () => {
    if (!startDate || !endDate) { setErr('Pick a start and end date.'); return; }
    setLoading(true); setErr('');
    try {
      const res = await api.get('/sales/reports/vat-return', {
        params: {
          start_date: startDate,
          end_date: endDate,
          corrections: Number(corrections || 0),
          carry_forward: Number(carryForward || 0),
        },
      });
      setReport(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const res = await api.get('/sales/reports/vat-return/export', {
        params: {
          start_date: startDate,
          end_date: endDate,
          corrections: Number(corrections || 0),
          carry_forward: Number(carryForward || 0),
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `vat_return_${startDate}_to_${endDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to download Excel');
    }
  };

  const printNow = () => window.print();

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="vat-return-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">📊 Bahrain VAT Return</h1>
          <p className="text-sm text-gray-600 mt-1">
            Auto-compiles the 18-line NBR form from your invoices and purchase entries.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={runReport} disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-lg shadow disabled:opacity-50"
                  data-testid="vat-run-btn">
            {loading ? 'Calculating…' : '▶ Generate'}
          </button>
          <button onClick={downloadExcel} disabled={!report}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-lg shadow disabled:opacity-50"
                  data-testid="vat-excel-btn">
            ⬇ Excel
          </button>
          <button onClick={printNow} disabled={!report}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
                  data-testid="vat-print-btn">
            🖨 PDF (Print)
          </button>
        </div>
      </div>

      {/* Filter panel */}
      <div className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-5 gap-3 print:hidden">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Period start</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                 className="w-full border rounded px-2 py-1.5 text-sm" data-testid="vat-start-date"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Period end</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                 className="w-full border rounded px-2 py-1.5 text-sm" data-testid="vat-end-date"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Corrections (BHD ±5,000)</label>
          <input type="number" step="0.001" value={corrections} placeholder="0.000"
                 onChange={(e) => setCorrections(e.target.value)}
                 className="w-full border rounded px-2 py-1.5 text-sm" data-testid="vat-corrections"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Credit carry-forward</label>
          <input type="number" step="0.001" value={carryForward} placeholder="0.000"
                 onChange={(e) => setCarryForward(e.target.value)}
                 className="w-full border rounded px-2 py-1.5 text-sm" data-testid="vat-carry-forward"/>
        </div>
        <div className="md:col-span-1 flex items-end text-xs text-gray-500">
          Auto-classifies records by their VAT %, country, and customs data.
          Override per record via the new "VAT Category" field.
        </div>
      </div>

      {/* Fiscal-year quick range — snaps both pickers to the selected FY in
          the global header, useful for the annual VAT summary. */}
      {selectedFy && (
        <div className="flex flex-wrap items-center gap-2 print:hidden -mt-1" data-testid="vat-fy-quick-row">
          <span className="text-xs text-gray-500">Quick range:</span>
          <button
            type="button"
            onClick={() => {
              setStartDate(selectedFy.start_date);
              setEndDate(selectedFy.end_date);
            }}
            className="text-xs px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 hover:bg-emerald-100"
            data-testid="vat-snap-fy-btn"
          >
            📅 Set to {selectedFy.name} (Jan 1 → Dec 31)
          </button>
        </div>
      )}

      {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">{err}</div>}

      {/* The actual report */}
      {report && (
        <div className="vat-print-area space-y-4 max-w-5xl mx-auto">
          {/* Title (matches the screenshot) */}
          <div className="bg-emerald-100 border border-emerald-300 rounded-t text-center py-2">
            <h2 className="text-xl font-bold text-gray-900">{(report.company_name || '').toUpperCase()}</h2>
            <p className="text-sm font-semibold text-gray-700">{periodLabel}</p>
          </div>

          {/* Sales table */}
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-emerald-700 text-white">
                <th className="border border-emerald-800 px-2 py-2 w-32 text-left underline">VAT on Sales</th>
                <th className="border border-emerald-800 px-2 py-2 text-center">Description</th>
                <th className="border border-emerald-800 px-2 py-2 text-center w-32">Amount (BHD)</th>
                <th className="border border-emerald-800 px-2 py-2 text-center w-40">Adjustment / Apportionment</th>
                <th className="border border-emerald-800 px-2 py-2 text-center w-32">VAT amount (BHD)</th>
              </tr>
            </thead>
            <tbody>
              {report.sales.map((r, i) => (
                <tr key={r.code} className={i % 2 ? 'bg-emerald-50' : 'bg-emerald-100/40'}>
                  <td className="border border-emerald-300 px-2 py-2 text-center bg-emerald-700 text-white font-bold">{i + 1}</td>
                  <td className="border border-emerald-300 px-2 py-2">{r.label}</td>
                  <td className="border border-emerald-300 px-2 py-2 text-right">{fmt(r.amount)}</td>
                  <td className="border border-emerald-300 px-2 py-2 text-right">0.00</td>
                  <td className="border border-emerald-300 px-2 py-2 text-right">{r.vat_amount ? fmt(r.vat_amount) : '-'}</td>
                </tr>
              ))}
              <tr className="bg-emerald-700 text-white font-bold">
                <td className="border border-emerald-800 px-2 py-2 text-center">7</td>
                <td className="border border-emerald-800 px-2 py-2">Total Sales</td>
                <td className="border border-emerald-800 px-2 py-2 text-right" data-testid="vat-total-sales-amount">{fmt(report.total_sales_amount)}</td>
                <td className="border border-emerald-800 px-2 py-2 text-right">0.00</td>
                <td className="border border-emerald-800 px-2 py-2 text-right" data-testid="vat-total-sales-vat">{fmt(report.total_sales_vat)}</td>
              </tr>
            </tbody>
          </table>

          {/* Purchases table */}
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-emerald-700 text-white">
                <th className="border border-emerald-800 px-2 py-2 w-32 text-left underline">VAT on Purchases</th>
                <th className="border border-emerald-800 px-2 py-2 text-center">Description</th>
                <th className="border border-emerald-800 px-2 py-2 text-center w-32">Amount (BHD)</th>
                <th className="border border-emerald-800 px-2 py-2 text-center w-40">Adjustment / Apportionment</th>
                <th className="border border-emerald-800 px-2 py-2 text-center w-32">VAT amount (BHD)</th>
              </tr>
            </thead>
            <tbody>
              {report.purchases.map((r, i) => (
                <tr key={r.code} className={i % 2 ? 'bg-emerald-50' : 'bg-emerald-100/40'}>
                  <td className="border border-emerald-300 px-2 py-2 text-center bg-emerald-700 text-white font-bold">{i + 8}</td>
                  <td className="border border-emerald-300 px-2 py-2">{r.label}</td>
                  <td className="border border-emerald-300 px-2 py-2 text-right">{fmt(r.amount)}</td>
                  <td className="border border-emerald-300 px-2 py-2 text-right">0.00</td>
                  <td className="border border-emerald-300 px-2 py-2 text-right">{r.vat_amount ? fmt(r.vat_amount) : '-'}</td>
                </tr>
              ))}
              <tr className="bg-emerald-700 text-white font-bold">
                <td className="border border-emerald-800 px-2 py-2 text-center">14</td>
                <td className="border border-emerald-800 px-2 py-2">Total Purchases</td>
                <td className="border border-emerald-800 px-2 py-2 text-right">{fmt(report.total_purchase_amount)}</td>
                <td className="border border-emerald-800 px-2 py-2 text-right">0.00</td>
                <td className="border border-emerald-800 px-2 py-2 text-right">{fmt(report.total_purchase_vat)}</td>
              </tr>
            </tbody>
          </table>

          {/* Summary block (lines 15-18) */}
          <table className="w-full text-sm border-collapse">
            <tbody>
              {[
                { num: 15, label: 'Total VAT Due for current period', val: report.vat_due_current, bold: true },
                { num: 16, label: 'Corrections from previous period (between BHD +/- 5,000)', val: report.corrections },
                { num: 17, label: 'VAT credit carried forward from previous period(s)', val: report.carry_forward },
                { num: 18, label: 'Net VAT due (or refundable)', val: report.net_vat_due, bold: true },
              ].map((r) => (
                <tr key={r.num} className={r.bold ? 'bg-emerald-100' : 'bg-emerald-50'}>
                  <td className="border border-emerald-300 px-2 py-2 text-center w-16 bg-emerald-700 text-white font-bold">{r.num}</td>
                  <td className={`border border-emerald-300 px-2 py-2 ${r.bold ? 'font-bold' : ''}`}>{r.label}</td>
                  <td className={`border border-emerald-300 px-2 py-2 text-right w-32 ${r.bold ? 'font-bold' : ''}`}
                      data-testid={`vat-line-${r.num}`}>{fmt(r.val)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="text-xs text-gray-500 italic">
            Counts in this run: {report.invoice_count} invoice(s), {report.purchase_count} purchase entry/entries
            within {report.start_date} → {report.end_date}.
          </p>
        </div>
      )}

      <style>{`
        @media print {
          body { background: #fff; }
          .print\\:hidden { display: none !important; }
          .vat-print-area { font-size: 10pt; }
        }
      `}</style>
    </div>
  );
}
